'use client';

import dynamic from 'next/dynamic';
import { type MessageKeys, useTranslations } from 'next-intl';
import { useState } from 'react';
import { renderRichListItem } from './richText';
import { VideoGroupPlayer, VideoGroupProvider } from './VideoGroupPlayer';

const Q3GeneratorInput = dynamic(
  () => import('../../_components/ProductInputTool/components/Q3GeneratorInput').then((mod) => mod.Q3GeneratorInput),
  { ssr: false },
);

export type DemoSectionType = {
  id: string;
  title: MessageKeys<IntlMessages, 'demo.demo1.title' | 'demo.demo2.title' | 'demo.demo3.title'>;
  performanceAnalysis: MessageKeys<
    IntlMessages,
    'demo.demo1.performanceAnalysis' | 'demo.demo2.performanceAnalysis' | 'demo.demo3.performanceAnalysis'
  >;
  performanceAnalysisContent: MessageKeys<
    IntlMessages,
    | 'demo.demo1.performanceAnalysisContent'
    | 'demo.demo2.performanceAnalysisContent'
    | 'demo.demo3.performanceAnalysisContent'
  >;
  media: {
    vidu: {
      videoSrc: string;
      videoPoster: string;
    };
    seedance: {
      videoSrc: string;
      videoPoster: string;
    };
    kling: {
      videoSrc: string;
      videoPoster: string;
    };
  };
  prompt: {
    text: string;
    image: string;
  };
};

export const DemoSection = ({
  title,
  performanceAnalysis,
  performanceAnalysisContent,
  media,
  prompt,
}: DemoSectionType) => {
  const t = useTranslations('vidu-q3-vs-seedance-2-0-vs-kling-3-0');
  const [img2VideoDuration, setImg2VideoDuration] = useState(10);
  const [text2VideoDuration, setText2VideoDuration] = useState(10);
  const [activeHoverId, setActiveHoverId] = useState<string | null>(null);

  return (
    <section className="relative mt-[88px] sm:mt-8">
      <div className="mx-auto w-[75vw] max-w-[1440px]">
        <h2 className="mb-10 text-center font-semibold text-[32px] text-white leading-[100%]">{t(title)}</h2>

        <div className="relative h-[536px]">
          <VideoGroupProvider value={{ activeHoverId, setActiveHoverId }}>
            <div className="grid h-full grid-cols-[2fr_1fr] grid-rows-2 gap-2">
              <div className="relative row-span-2 h-full overflow-hidden bg-system-white/60">
                <VideoGroupPlayer
                  id="vidu"
                  uri={media.vidu.videoSrc}
                  coverUri={media.vidu.videoPoster}
                  muted={false}
                  autoPlayWhenInCenter
                  showMaskBg
                />
                <span className="absolute top-2 right-2 z-10 rounded-8 bg-system-white/5 px-2 py-1 text-system-white text-xs backdrop-blur-[100px]">
                  Vidu Q3
                </span>
              </div>
              <div className="relative h-full overflow-hidden bg-system-white/60">
                <VideoGroupPlayer
                  id="seedance"
                  uri={media.seedance.videoSrc}
                  coverUri={media.seedance.videoPoster}
                  muted={false}
                  showMaskBg
                />
                <span className="absolute top-2 right-2 z-10 rounded-8 bg-system-white/5 px-2 py-1 text-system-white text-xs backdrop-blur-[100px]">
                  Seedance 2.0
                </span>
              </div>
              <div className="relative h-full overflow-hidden bg-system-white/60">
                <VideoGroupPlayer
                  id="kling"
                  uri={media.kling.videoSrc}
                  coverUri={media.kling.videoPoster}
                  muted={false}
                  showMaskBg
                />
                <span className="absolute top-2 right-2 z-10 rounded-8 bg-system-white/5 px-2 py-1 text-system-white text-xs backdrop-blur-[100px]">
                  Kling 3.0
                </span>
              </div>
            </div>
          </VideoGroupProvider>

          <div className="-translate-x-1/2 absolute bottom-0 left-1/2 w-[50vw] max-w-[720px] translate-y-1/2">
            <Q3GeneratorInput
              className="w-full"
              activeTab="img2video"
              hideTabSwitch
              displayPrompt={{ text: prompt.text, image: prompt.image }}
              submitButtonText={t('demo.submitButton')}
              img2VideoDuration={img2VideoDuration}
              text2VideoDuration={text2VideoDuration}
              onImg2VideoDurationChange={setImg2VideoDuration}
              onText2VideoDurationChange={setText2VideoDuration}
            />
          </div>
        </div>

        <div className="mt-[150px]">
          <h2 className="mb-2 font-semibold text-white text-xl">{t(performanceAnalysis)}</h2>
          <div className="text-base text-system-white64 leading-[160%]">
            {t.rich(performanceAnalysisContent, {
              p: (chunks) => <p>{chunks}</p>,
              li: renderRichListItem,
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
